import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@bynder/localization';
import { Button, Dropdown, token, Tooltip } from '@bynder/design-system';
import { IconCancel, IconCheck, IconError } from '@bynder/icons';
import {
    getImportTemplateAnalysis,
    getImportTemplateErrors,
    getImportTemplateMultiDesignMode,
    getImportTemplateTargetDpi,
    getImportTemplateValidationErrors,
} from 'packages/store/importTemplate/importTemplate.selectors';
import {
    DesignFileAnalysisGlobal,
    DesignFileAnalysisLocal,
    errorKeys,
    IMPORT_TARGET_DPI_OPTIONS,
} from 'packages/store/importTemplate/types';
import { formatBytes } from 'packages/pages/utils';
import { setImportDesignMode, setImportTargetDpi } from 'packages/store/importTemplate/importTemplate.actions';
import {
    getScaledArtboardDimensions,
    isIndesignDesignFileAnalysis,
    isScaledArtboardOverMaxSize,
} from 'packages/store/importTemplate/utils';
import useAccessRights from 'packages/hooks/useAccessRights';
import { envVar } from '~/helpers/envVar';
import features from '~/configs/features';
import {
    DropdownLabel,
    DropdownSection,
    Column,
    ColumnHeader,
    DetailLabel,
    DetailsGroup,
    DetailValue,
    IconContainer,
    ImportContainer,
    ImportInner,
    PageItem,
    PagesGroup,
    StyledIcon,
    Title,
    TwoColumnLayout,
} from './Import.styled';

const ImportStatusDesignFile = () => {
    const { translate } = useTranslate();
    const analysis = useSelector(getImportTemplateAnalysis);
    const errors = useSelector(getImportTemplateErrors);
    const validationErrors = useSelector(getImportTemplateValidationErrors);
    const multiDesignMode = useSelector(getImportTemplateMultiDesignMode);
    const targetDpi = useSelector(getImportTemplateTargetDpi);
    const dispatch = useDispatch();
    const { isPlatformAllowed } = useAccessRights();

    const isMultiArtboard = analysis?.designCreationResponses.length > 1;
    const isIndesignImport = isIndesignDesignFileAnalysis(analysis);
    const maxResolutionError = translate('modal.design.create.design.import.file.max_resolution');

    const displayedArtboards =
        analysis?.designCreationResponses.map((artboard) => {
            const dimensions = isIndesignImport ? getScaledArtboardDimensions(artboard, targetDpi) : artboard;

            return {
                ...artboard,
                dimensions,
                hasScaledMaxSizeWarning:
                    isIndesignImport && isScaledArtboardOverMaxSize(artboard, targetDpi, envVar.imageMaxDimensions),
            };
        }) || [];

    const getFormattedDimensions = (item: { width: number; height: number }) => `${item.width} × ${item.height} px`;

    const getErrorIcon = (error: string) => (
        <Tooltip content={error}>
            <IconError style={{ color: token.red500 }} size="24" />
        </Tooltip>
    );

    const getWarningIcon = (warning: string) => (
        <Tooltip content={warning}>
            <IconError data-testid="Import warning icon" style={{ color: token.yellow700 }} size="20" />
        </Tooltip>
    );

    const getTemplateValue = () => {
        if (!analysis) {
            return { name: '', extension: undefined };
        }

        if ('extensions' in analysis) {
            const file: DesignFileAnalysisGlobal = analysis;

            return { name: file.name, extension: file.extensions?.[0] };
        }

        const file: DesignFileAnalysisLocal = analysis;
        let name = file.fileName;
        const extFromName = file.fileName.split('.').pop();
        const lastDotIdx = file.fileName.lastIndexOf('.');

        if (lastDotIdx !== -1 && extFromName === file.extension) {
            name = file.fileName.slice(0, lastDotIdx);
        }

        return { name, extension: file.extension };
    };

    const { name, extension } = getTemplateValue();

    const templateName = {
        value: (analysis && `${name}.${extension}`) || (errors && errors.name),
        error: () => {
            if (validationErrors.includes(errorKeys.EMPTY_NAME)) {
                return translate('modal.design.create.design.import.file.empty_name');
            }

            return '';
        },
    };

    const getDimensionError = (): string => {
        const templateErrors = validationErrors.filter((error) => !Array.isArray(error));

        if (templateErrors.length) {
            if (templateErrors.includes(errorKeys.MIN_SIZE)) {
                return translate('modal.design.create.design.import.file.min_size');
            }

            if (templateErrors.includes(errorKeys.MIN_RESOLUTION)) {
                return translate('modal.design.create.design.import.file.min_resolution');
            }

            if (templateErrors.includes(errorKeys.MAX_RESOLUTION)) {
                return maxResolutionError;
            }
        }

        return '';
    };

    const getDimensionWarning = (): string => {
        if (!isMultiArtboard && displayedArtboards[0]?.hasScaledMaxSizeWarning) {
            return maxResolutionError;
        }

        return '';
    };

    const getArtboardError = (index: number): string => {
        const artboardErrors = validationErrors.filter(
            (error) => Array.isArray(error) && error[0].artboardIndex === index,
        );
        const errorArray: string[] = [];

        if (artboardErrors.length > 0) {
            if (artboardErrors[0].some(({ error }) => error && error === errorKeys.MIN_SIZE)) {
                errorArray.push(translate('modal.design.create.design.import.file.min_size'));
            }

            if (artboardErrors[0].some(({ error }) => error && error === errorKeys.MIN_RESOLUTION)) {
                errorArray.push(translate('modal.design.create.design.import.file.min_resolution'));
            }

            if (artboardErrors[0].some(({ error }) => error && error === errorKeys.MAX_RESOLUTION)) {
                errorArray.push(maxResolutionError);
            }
        }

        return errorArray.join('. ');
    };

    const getArtboardWarning = (index: number): string => {
        return displayedArtboards[index]?.hasScaledMaxSizeWarning ? maxResolutionError : '';
    };

    const renderItemStatusIcon = (errorMessage: string, warningMessage?: string) => {
        if (errorMessage) {
            return <StyledIcon>{getErrorIcon(errorMessage)}</StyledIcon>;
        }

        if (warningMessage) {
            return <StyledIcon>{getWarningIcon(warningMessage)}</StyledIcon>;
        }

        return null;
    };

    const showPagesColumn = isMultiArtboard && displayedArtboards.length > 0;
    const showConvertSelector = isPlatformAllowed([features.MULTI_SIZE_CREATIVE]) && showPagesColumn;

    const onImportModeSelect = (isMultiDesign: boolean) => {
        dispatch(setImportDesignMode(isMultiDesign));
    };

    const onTargetDpiSelect = (nextTargetDpi) => {
        dispatch(setImportTargetDpi(nextTargetDpi));
    };

    return (
        <ImportContainer id="new_design_modal_import_status_design" data-testid="Import status container">
            <ImportInner>
                <IconContainer data-testid="Import status icon" $importStatus={!errors && !validationErrors.length}>
                    {!errors && !validationErrors.length ? <IconCheck /> : <IconCancel />}
                </IconContainer>
                <Title id="new_design_modal_import_status_design_title" data-testid="Import status title">
                    {templateName.value}
                    {templateName.error() && <StyledIcon>{getErrorIcon(templateName.error())}</StyledIcon>}
                </Title>
                {analysis ? (
                    <TwoColumnLayout>
                        <Column data-testid="Import general details">
                            <ColumnHeader>{translate('modal.design.create.design.import.file.details')}</ColumnHeader>

                            <DetailsGroup>
                                <div>
                                    <DetailLabel>
                                        {translate('modal.design.create.design.import.file.type')}:{' '}
                                    </DetailLabel>
                                    <DetailValue>
                                        {translate('modal.design.create.design.import.file.type.image')}
                                    </DetailValue>
                                </div>
                                <div>
                                    <DetailLabel>
                                        {translate('modal.design.create.design.import.file.file_size')}:{' '}
                                    </DetailLabel>
                                    <DetailValue>{formatBytes(analysis.fileSize)}</DetailValue>
                                </div>
                                {!isMultiArtboard && displayedArtboards[0] ? (
                                    <div>
                                        <DetailLabel>
                                            {translate('modal.design.create.design.import.file.dimensions')}:{' '}
                                        </DetailLabel>
                                        <DetailValue>
                                            {getFormattedDimensions(displayedArtboards[0].dimensions)}
                                        </DetailValue>
                                        {renderItemStatusIcon(getDimensionError(), getDimensionWarning())}
                                    </div>
                                ) : null}
                            </DetailsGroup>

                            {isIndesignImport ? (
                                <DropdownSection>
                                    <DropdownLabel>{translate('modal.design.create.design.import.dpi')}</DropdownLabel>
                                    <Dropdown
                                        trigger={({ isOpen, ...triggerProps }) => (
                                            <Button
                                                isFullWidth
                                                aria-label={translate('modal.design.create.design.import.dpi')}
                                                rightIcon={<Dropdown.Arrow />}
                                                {...triggerProps}
                                            >
                                                <Dropdown.SelectButtonText>{targetDpi}</Dropdown.SelectButtonText>
                                            </Button>
                                        )}
                                    >
                                        {IMPORT_TARGET_DPI_OPTIONS.map((dpi) => (
                                            <Dropdown.Item
                                                key={dpi}
                                                onClick={() => onTargetDpiSelect(dpi)}
                                                isChecked={targetDpi === dpi}
                                            >
                                                {dpi}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown>
                                </DropdownSection>
                            ) : null}

                            {showConvertSelector ? (
                                <DropdownSection>
                                    <DropdownLabel>
                                        {translate('modal.design.create.design.import.convert')}
                                    </DropdownLabel>
                                    <Dropdown
                                        trigger={({ isOpen, ...triggerProps }) => (
                                            <Button
                                                isFullWidth
                                                aria-label={translate('modal.design.create.design.import.multidesign')}
                                                data-testid="design create : multidesign button"
                                                rightIcon={<Dropdown.Arrow />}
                                                {...triggerProps}
                                            >
                                                <Dropdown.SelectButtonText>
                                                    {!multiDesignMode
                                                        ? translate('modal.design.create.design.import.pages')
                                                        : translate('modal.design.create.design.import.designs')}
                                                </Dropdown.SelectButtonText>
                                            </Button>
                                        )}
                                    >
                                        <Dropdown.Item
                                            onClick={() => onImportModeSelect(false)}
                                            isChecked={!multiDesignMode}
                                        >
                                            {translate('modal.design.create.design.import.pages')}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() => onImportModeSelect(true)}
                                            isChecked={multiDesignMode}
                                        >
                                            {translate('modal.design.create.design.import.designs')}
                                        </Dropdown.Item>
                                    </Dropdown>
                                </DropdownSection>
                            ) : null}
                        </Column>

                        {showPagesColumn ? (
                            <Column data-testid="Import artboards details">
                                <ColumnHeader data-testid="Import artboard title">
                                    {translate('modal.design.create.design.import.file.artboards')}
                                </ColumnHeader>

                                <PagesGroup>
                                    {displayedArtboards.map((item, index) => {
                                        const artboardError = getArtboardError(index);
                                        const artboardWarning = getArtboardWarning(index);

                                        return (
                                            <PageItem key={item.name} data-testid="Import page item">
                                                <DetailValue>
                                                    {item.name}:{' '}
                                                    <strong>{getFormattedDimensions(item.dimensions)}</strong>
                                                </DetailValue>
                                                {renderItemStatusIcon(artboardError, artboardWarning)}
                                            </PageItem>
                                        );
                                    })}
                                </PagesGroup>
                            </Column>
                        ) : null}
                    </TwoColumnLayout>
                ) : null}
            </ImportInner>
        </ImportContainer>
    );
};

export default ImportStatusDesignFile;
